import DataService from '@shared/services/data.service';
import TagsService from '../../Tags/services/tags.service';

/**
 * Shared list/filter plumbing for the Webpages lists (active + archived tabs).
 *
 * Extracted from WebpageList.vue / ArchivedWebpageList.vue so the two lists stay in
 * sync. A consuming component must provide the following reactive state:
 *   - filterParams: { search, combinedFilter, ... }
 *   - pagin: { current, pageSize, total }
 *   - selectedRowKeys: number[]
 *   - data: any[]
 * and implement getList().
 */
export default {
  data() {
    return {
      availableTags: [],
      tagsMap: {},
      enumsData: null,
    };
  },
  computed: {
    featureAccess() {
      return (this.$store && this.$store.state && this.$store.state.user && this.$store.state.user.data && this.$store.state.user.data.feature_access) || {};
    },
    isTagsFeatureEnabled() {
      return this.featureAccess && this.featureAccess['tags'];
    },
    // ---> API enums > [planTypeOptions] > plan filter dropdowns & plan/polling pickers
    planTypeOptions() {
      const planTypes = (this.enumsData && this.enumsData.url && this.enumsData.url.plan_type) || [];
      return planTypes.map((value) => ({
        label: value.charAt(0) + value.slice(1).toLowerCase(),
        value,
      }));
    },
    isHasSelected() {
      return this.selectedRowKeys.length > 0;
    },
    selectedRowKeysSet() {
      return new Set(this.selectedRowKeys);
    },
    hasCombinedFilterActive() {
      return Array.isArray(this.filterParams.combinedFilter) && this.filterParams.combinedFilter.length > 0;
    },
    // ---> filterParams.combinedFilter > [parsedCombinedFilter] > getList()
    // Superset parser: the archived list simply ignores `statuses`.
    parsedCombinedFilter() {
      const result = { statuses: [], planTypes: [], tags: [] };
      const combined = (this.filterParams && this.filterParams.combinedFilter) || [];
      combined.forEach((val) => {
        if (val.startsWith('status:')) {
          result.statuses.push(val.replace('status:', ''));
        } else if (val.startsWith('planType:')) {
          result.planTypes.push(val.replace('planType:', ''));
        } else if (val.startsWith('tag:')) {
          result.tags.push(val.replace('tag:', ''));
        }
      });
      return result;
    },
  },
  methods: {
    // Load enums once (plan types + per-plan polling intervals) for the filters/pickers.
    async loadEnums() {
      try {
        this.enumsData = await DataService.getEnums();
      } catch (error) {
        console.error('Failed to load enums:', error);
      }
    },
    getTagIdsByNames(tagNames) {
      if (!Array.isArray(tagNames) || tagNames.length === 0 || !this.tagsMap) {
        return [];
      }
      return tagNames.map((name) => this.tagsMap[name]).filter(Boolean);
    },
    // Fetch the tag filter dropdown once, then reuse. Returns the request promise
    // (or a resolved null) so it can be composed into the getList() Promise.all.
    loadTagsIfNeeded() {
      if (this.availableTags.length > 0) {
        return Promise.resolve(null);
      }
      return TagsService.getList((this.$store.state.user.data || {}).id);
    },
    // Bootstrap tagsMap + availableTags from a tags response (no-op when null).
    applyTagsData(tagsData) {
      if (!tagsData) {
        return;
      }
      this.tagsMap = {};
      tagsData.forEach((tag) => {
        this.tagsMap[tag.name] = tag.id;
      });
      this.availableTags = tagsData.map((tag) => tag.name);
    },
    // Map a raw list response into table rows with index / isChecked / tags.
    mapListRows(items) {
      const startIndex = (this.pagin.current - 1) * this.pagin.pageSize;
      return items.map((item, index) => ({
        ...item,
        index: startIndex + index + 1,
        isChecked: this.selectedRowKeysSet.has(item.id),
        tags: Array.isArray(item.tags) ? item.tags : [],
      }));
    },
    onChangeCombinedFilter(selectedValues) {
      this.filterParams.combinedFilter = Array.isArray(selectedValues) ? selectedValues : [];
      this.pagin.current = 1;
      this.getList();
    },
    paginChange(pagin) {
      this.pagin = { ...pagin };
      this.getList();
    },
    // Debounced onSearch stays defined per-component (so each list keeps its own
    // debounce timer); it delegates the body here.
    runSearch(event) {
      const searchTerm = (event.target.value && String(event.target.value).trim().toLowerCase()) || '';
      this.filterParams.search = searchTerm;
      this.pagin.current = 1;
      this.getList();
    },
    onChangeExactMatch() {
      this.pagin.current = 1;
      this.getList();
    },
    onSelectChange(selectedRowKeys, { replaceAll = false } = {}) {
      if (replaceAll) {
        this.selectedRowKeys = selectedRowKeys;
        return;
      }
      // Merge: keep selections from other pages, update only the current page.
      const currentPageIdSet = new Set(this.data.map((item) => item.id));
      const otherPageSelections = this.selectedRowKeys.filter((key) => !currentPageIdSet.has(key));
      this.selectedRowKeys = [...otherPageSelections, ...selectedRowKeys];
    },
    // Resolve valid polling-interval options for a plan type and keep `form.pollingInterval`
    // valid. Shared by the active-tab bulk edit card and the archived-tab restore card.
    // Mutates form.pollingInterval in place and returns the options map for the dropdown.
    applyPollingOptionsForPlan(form, planType) {
      let options = {};
      if (planType && this.enumsData && this.enumsData.url && this.enumsData.url.polling_interval) {
        options = this.enumsData.url.polling_interval[planType] || {};
      }
      const validOptions = Object.keys(options).map((k) => +k).sort((a, b) => a - b);
      if (validOptions.length > 0) {
        if (!form.pollingInterval || !validOptions.includes(form.pollingInterval)) {
          form.pollingInterval = validOptions[0];
        }
      } else {
        form.pollingInterval = null;
      }
      return options;
    },
  },
};
